import { useLayoutEffect, type RefObject } from 'react';

/**
 * I-2135 (IU-3, ruled c): in a phone browser `<main>` scrolls the page (IU-1), and the top row
 * overlays it, as it overlays the document in the app's shell. The stack that holds the row gives
 * its room back to `<main>` (a negative bottom margin of its own height), `<main>` opens with a
 * spacer of that height, so its content rests under the row and scrolls under it, and its
 * `scroll-padding-top` is that height, so an anchor or a focus moved into view stops under the row.
 * The row slides out over `<main>`'s own content, never over a room `<main>` cannot fill.
 *
 * The room is the stack's height, measured, on the document as `--phone-row-room`: none where the
 * row does not overlay `<main>` (the app, where the document scrolls, or no phone row).
 */
export const ROW_ROOM_PROPERTY = '--phone-row-room';
/** The stack's mark while its row overlays `<main>`. */
export const ROW_OVERLAYS_MAIN = 'overlay';
export const OVERLAY_STACK_SELECTOR = `[data-phone-chrome-stack="${ROW_OVERLAYS_MAIN}"]`;

/** Whether the phone's row overlays `<main>` now (a phone browser with a row). */
export function rowOverlaysMain(): boolean {
  return document.querySelector(OVERLAY_STACK_SELECTOR) !== null;
}

/**
 * Where the page under an overlaying row is first seen, on the screen: the stack's resting bottom.
 * The row moves out of the stack by its own `top`; the stack's box never moves, so a scroll does not
 * change this (TS-11's stop reads it). `null` where the row does not overlay `<main>`.
 */
export function overlayRestingBottom(): number | null {
  const stack = document.querySelector(OVERLAY_STACK_SELECTOR);
  return stack ? stack.getBoundingClientRect().bottom : null;
}

export function usePhoneRowRoom(stack: RefObject<HTMLElement | null>, overlays: boolean): void {
  useLayoutEffect(() => {
    const element = stack.current;
    const root = document.documentElement;
    if (!element || !overlays) {
      root.style.removeProperty(ROW_ROOM_PROPERTY);
      return;
    }
    // The border box: the row's padding is part of the room it takes.
    const publish = () => root.style.setProperty(ROW_ROOM_PROPERTY, `${element.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(element, { box: 'border-box' });
    return () => {
      observer?.disconnect();
      root.style.removeProperty(ROW_ROOM_PROPERTY);
    };
  }, [stack, overlays]);
}
